import {
  Box,
  Button,
  Divider,
  Flex,
  FlexProps,
  IconButton,
  Link,
  LinkProps,
  Spacer,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  RiChat1Line,
  RiMenuSearchLine,
  RiRouteLine,
  RiSearchLine,
  RiSoundModuleLine,
} from "@remixicon/react";
import { ReactElement, ReactNode, useCallback } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";

import OrgSuspendedRibbon from "@/admin/components/OrgSuspendedRibbon";
import { AppSwitcher } from "@/app/components/AppPicker";
import { openCommandBar } from "@/app/components/CommandBar";
import { EnvironmentPicker } from "@/app/components/EnvironmentPicker";
import { Restricted } from "@/app/components/Restricted";
import { useOnboardingProgress } from "@/app/hooks/useOnboardingProgress";
import { IcoFeature, IcoSegment } from "@/common/components/Icons";
import { NavLink } from "@/common/components/NavLink";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { openIntercom } from "@/common/utils/intercom";
import NewFeature from "@/feature/pages/NewFeature";
import { TrialStatus } from "@/global-settings/components/TrialStatus";
import { useFlag } from "@reflag/react-sdk";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { FeatureUrl, GlobalSettingsUrl } from "@reflag/shared/urls";

import { AppFooter } from "./AppFooter";

export default function AppSidebar({ ...props }: FlexProps) {
  const { user, org } = useEnvironment();

  return (
    <Flex
      {...props}
      aria-label="Main"
      as="nav"
      direction="column"
      gap={3.5}
      pb={3}
      pt={2}
      px={3.5}
    >
      {user?.isReflagAdmin && org.suspendedAt !== null && (
        <OrgSuspendedRibbon />
      )}
      <AppSwitcher />
      <Flex flexShrink={0} gap={1.5}>
        <TrackFeature />
        <SearchButton />
      </Flex>
      <MainMenu />
      <Spacer />
      <EnvironmentPicker />
      <Divider />
      <TrialBanner />
      <HelpLinks />
      <AppFooter />
    </Flex>
  );
}

function TrackFeature() {
  const { env } = useEnvironment();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useNavigate();

  const onSuccess = useCallback(
    (feature: FeatureDetail) => {
      onClose();
      navigate(FeatureUrl(env, feature));
    },
    [env, navigate, onClose],
  );

  return (
    <Restricted role="contributor" _tooltipWrapper={{ flex: 1 }}>
      <Button flex="1" size="sm" variant="outline" w="full" onClick={onOpen}>
        New flag
      </Button>
      <NewFeature
        isOpen={isOpen}
        motionPreset="slideInLeft"
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Restricted>
  );
}

function SearchButton() {
  const { isEnabled } = useFlag("command-bar");

  if (!isEnabled) {
    return null;
  }

  return (
    <Tooltip label="Search (Cmd/Ctrl+K)">
      <IconButton
        aria-label="Search"
        icon={<RiSearchLine size={15} />}
        size="sm"
        variant="outline"
        onClick={openCommandBar}
      />
    </Tooltip>
  );
}

function MainMenu() {
  const location = useLocation();
  const { isEnabled: onboardingEnabled } = useFlag("onboarding");
  const isOnboardingPath = location.pathname.endsWith("/onboarding");
  const { hasFlags, menuProgress } = useOnboardingProgress({
    enabled: onboardingEnabled || isOnboardingPath,
    forceFullStatus: isOnboardingPath,
  });
  const showOnboarding = onboardingEnabled && (!hasFlags || isOnboardingPath);

  return (
    <Flex
      align="stretch"
      direction="column"
      fontSize="sm"
      gap={0.5}
      justifyContent="start"
    >
      <MenuHeading icon={<IcoFeature boxSize="15px" />} to="flags">
        Flags
      </MenuHeading>

      <MenuHeading icon={<IcoSegment boxSize="15px" />} to="segments">
        Segments
      </MenuHeading>

      <MenuHeading icon={<RiChat1Line size={15} />} to="feedback">
        Feedback
      </MenuHeading>

      <MenuHeading icon={<RiMenuSearchLine size={15} />} to="event-log">
        Event log
      </MenuHeading>

      <MenuHeading icon={<RiSoundModuleLine size={15} />} to="settings">
        Settings
      </MenuHeading>

      {showOnboarding && (
        <MenuHeading icon={<RiRouteLine size={15} />} to="onboarding">
          Onboarding {menuProgress.completed}/{menuProgress.total}
        </MenuHeading>
      )}
    </Flex>
  );
}

function MenuHeading({
  children,
  icon,
  to,
}: {
  children: ReactNode;
  icon?: ReactElement;
  manageButton?: ReactElement;
  to?: string;
}) {
  const activeBg = useColorModeValue("gray.100", "gray.850");
  const hoverText = useColorModeValue("gray.900", "gray.50");

  return (
    <Flex
      _hover={{ color: hoverText }}
      align="center"
      as={NavLink}
      borderRadius="md"
      color="dimmed"
      end={false}
      gap={1.5}
      position="relative"
      px={2}
      py={1.5}
      sx={{
        '&[aria-current="page"]': {
          bg: activeBg,
          color: hoverText,
        },
      }}
      to={to}
      transition="all 0.1s ease-in-out"
    >
      <Box flex="0 0 auto">{icon}</Box>
      <Box
        flex="1 1 auto"
        fontWeight="medium"
        overflow="hidden"
        whiteSpace="nowrap"
        data-menu-heading
        isTruncated
      >
        {children}
      </Box>
    </Flex>
  );
}

export function FooterLink({ children, ...props }: LinkProps) {
  return (
    <Link
      {...props}
      _hover={{ color: useColorModeValue("gray.900", "white") }}
      color="dimmed"
      fontSize="sm"
      fontWeight="medium"
      px={2}
      textDecoration="none"
    >
      {children}
    </Link>
  );
}

function TrialBanner() {
  const hoverColor = useColorModeValue("gray.900", "white");

  const { env, org } = useEnvironment();

  if (org.trialEndsAt === null) {
    return null;
  }

  return (
    <Link
      _hover={{ color: hoverColor }}
      as={RouterLink}
      color="dimmed"
      fontWeight="medium"
      px={2}
      py={1.5}
      textDecoration="none"
      to={GlobalSettingsUrl(env, "org-billing")}
    >
      <TrialStatus />
    </Link>
  );
}

function HelpLinks() {
  const { requestFeedback } = useFlag("global");

  return (
    <Flex align="flex-start" direction="column" gap={1}>
      <FooterLink
        px={2}
        textDecoration="none"
        onClick={(e) => {
          e.preventDefault();
          requestFeedback({
            title: "How do you like Reflag?",
            position: {
              type: "POPOVER",
              anchor: e.currentTarget,
            },
            openWithCommentVisible: true,
          });
        }}
      >
        Give feedback
      </FooterLink>

      <FooterLink
        href="mailto:hello@reflag.com"
        px={2}
        textDecoration="none"
        onClick={(e) => {
          e.preventDefault();
          openIntercom();
        }}
      >
        Chat with us
      </FooterLink>

      <FooterLink
        href="https://reflag.com/changelog"
        px={2}
        target="_blank"
        textDecoration="none"
      >
        Changelog
      </FooterLink>
      <FooterLink
        href="https://docs.reflag.com"
        px={2}
        target="_blank"
        textDecoration="none"
      >
        Documentation
      </FooterLink>
    </Flex>
  );
}
